import { API } from '@/config/environment';

interface HubSpotField {
  objectTypeId: string;
  name: string;
  value: string;
}

interface HubSpotSubmission {
  portalId: string;
  formGuid: string;
  fields: Record<string, string>;
}

/**
 * Submit a form to HubSpot via the Forms API v3.
 * This is a public endpoint — no secret API keys required.
 */
export async function submitToHubSpot({ portalId, formGuid, fields }: HubSpotSubmission): Promise<void> {
  if (!portalId || !formGuid) {
    console.warn('[HubSpot] Portal ID or Form GUID not configured. Skipping submission.');
    return;
  }

  // organization_type is a company (0-2) property in HubSpot; all others are contact (0-1)
  const COMPANY_FIELDS = new Set(['organization_type']);

  const hsFields: HubSpotField[] = Object.entries(fields).map(([name, value]) => ({
    objectTypeId: COMPANY_FIELDS.has(name) ? '0-2' : '0-1',
    name,
    value,
  }));

  const response = await fetch(
    `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formGuid}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: hsFields,
        context: {
          pageUri: window.location.href,
          pageName: document.title,
        },
      }),
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HubSpot submission failed: ${response.status} ${text}`);
  }
}

export async function submitDemoRequest(fields: Record<string, string>): Promise<void> {
  return submitToHubSpot({
    portalId: API.HUBSPOT_PORTAL_ID,
    formGuid: API.HUBSPOT_DEMO_FORM_GUID,
    fields,
  });
}

export async function submitContactForm(fields: Record<string, string>): Promise<void> {
  return submitToHubSpot({
    portalId: API.HUBSPOT_PORTAL_ID,
    formGuid: API.HUBSPOT_CONTACT_FORM_GUID,
    fields,
  });
}
